import { SELFIE_CONFIG as C } from './config'

/** Minimal landmark shape (MediaPipe NormalizedLandmark is compatible). */
export interface Point {
  x: number
  y: number
  z?: number
}

export interface FaceBox {
  x: number
  y: number
  w: number
  h: number
}

/** Bounding box of all landmarks, in normalized coordinates. */
export function faceBox(landmarks: Point[]): FaceBox {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const p of landmarks) {
    if (p.x < minX) minX = p.x
    if (p.y < minY) minY = p.y
    if (p.x > maxX) maxX = p.x
    if (p.y > maxY) maxY = p.y
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
}

export function isCloseEnough(box: FaceBox): boolean {
  return box.w >= C.minFaceWidth
}

export function isTooClose(box: FaceBox): boolean {
  return (
    box.w > C.maxFaceWidth ||
    box.x < C.edgeMargin ||
    box.y < C.edgeMargin ||
    box.x + box.w > 1 - C.edgeMargin ||
    box.y + box.h > 1 - C.edgeMargin
  )
}

export function isCentred(box: FaceBox): boolean {
  return (
    Math.abs(box.x + box.w / 2 - 0.5) <= C.maxCentreOffsetX &&
    Math.abs(box.y + box.h / 2 - 0.5) <= C.maxCentreOffsetY
  )
}

export interface PixelStats {
  /** Mean luminance 0..255. */
  meanLuma: number
  /** Variance of the Laplacian of the grayscale image. */
  sharpness: number
}

/** Luminance + Laplacian-variance sharpness from RGBA pixels. */
export function pixelStats(img: {
  data: ArrayLike<number>
  width: number
  height: number
}): PixelStats {
  const { data, width, height } = img
  const n = width * height
  const gray = new Float32Array(n)
  let sum = 0
  for (let i = 0; i < n; i++) {
    const g =
      0.299 * (data[i * 4] ?? 0) + 0.587 * (data[i * 4 + 1] ?? 0) + 0.114 * (data[i * 4 + 2] ?? 0)
    gray[i] = g
    sum += g
  }
  if (width < 3 || height < 3) return { meanLuma: n ? sum / n : 0, sharpness: 0 }
  let lapSum = 0
  let lapSq = 0
  let count = 0
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x
      const lap =
        (gray[i - 1] ?? 0) +
        (gray[i + 1] ?? 0) +
        (gray[i - width] ?? 0) +
        (gray[i + width] ?? 0) -
        4 * (gray[i] ?? 0)
      lapSum += lap
      lapSq += lap * lap
      count++
    }
  }
  const mean = lapSum / count
  return { meanLuma: sum / n, sharpness: lapSq / count - mean * mean }
}

export type HintKey = 'noFace' | 'multipleFaces' | 'tooFar' | 'tooClose' | 'offCentre'

export interface FrameChecks {
  oneFace: boolean
  closeEnough: boolean
  centred: boolean
  lightSharp: boolean
}

export interface FrameEvaluation {
  checks: FrameChecks
  /** Single highest-priority problem, or null when quality holds. */
  hint: HintKey | null
  /** At least `minPassedChecks` of the 4 checks pass. */
  ok: boolean
}

const NO_CHECKS: FrameChecks = {
  oneFace: false,
  closeEnough: false,
  centred: false,
  lightSharp: false,
}

/**
 * Pure per-frame quality evaluation. `faces` are the landmark sets of every
 * detected face; `stats` are the pixel stats of the face box (may be absent
 * when not measured, in which case light/sharpness is not judged).
 */
export function evaluateFrame(faces: Point[][], stats: PixelStats | null): FrameEvaluation {
  if (faces.length === 0) return { checks: NO_CHECKS, hint: 'noFace', ok: false }
  if (faces.length > 1) return { checks: NO_CHECKS, hint: 'multipleFaces', ok: false }

  const lm = faces[0]
  if (!lm) return { checks: NO_CHECKS, hint: 'noFace', ok: false }
  const box = faceBox(lm)
  const closeEnough = isCloseEnough(box)
  const tooClose = isTooClose(box)
  const centred = isCentred(box)
  const lightSharp =
    !!stats &&
    stats.meanLuma >= C.minLuma &&
    stats.meanLuma <= C.maxLuma &&
    stats.sharpness >= C.minSharpness

  const checks: FrameChecks = {
    oneFace: true,
    closeEnough: closeEnough && !tooClose,
    centred,
    lightSharp,
  }

  const passed = Object.values(checks).filter(Boolean).length
  const ok = passed >= C.minPassedChecks

  // Not ok means two of the three checks below fail, so a framing problem
  // always exists; it is the hint the person can act on first.
  let hint: HintKey | null = null
  if (!ok) hint = !closeEnough ? 'tooFar' : tooClose ? 'tooClose' : 'offCentre'

  return { checks, hint, ok }
}
